import { ChangeDetectionStrategy, Component, inject, OnInit, OnDestroy, ChangeDetectorRef, ViewChild, ElementRef } from '@angular/core';
import { FormsModule, ReactiveFormsModule, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogRef, MatDialogModule } from '@angular/material/dialog';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatSnackBar } from '@angular/material/snack-bar';
import { CommonModule } from '@angular/common';
import { MatDialog } from '@angular/material/dialog';
import { CourtsService } from '../courts.service';
import { Subject, takeUntil } from 'rxjs';
import { ImageViewerDialogComponent, ImageViewerDialogData } from './image-viewer-dialog.component';
import { normalizeSportIdentity } from 'app/shared/sport-display';

@Component({
    selector: 'app-edit-court-dialog',
    templateUrl: './edit-court-dialog.component.html',
    styleUrl: './edit-court-dialog.component.scss',
    imports: [
        CommonModule,
        MatFormFieldModule,
        ReactiveFormsModule,
        MatInputModule,
        FormsModule,
        MatButtonModule,
        MatCheckboxModule,
        MatIconModule,
        MatSelectModule,
        MatDialogModule,
        MatSlideToggleModule,
        MatTooltipModule
    ],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EditCourtDialogComponent implements OnInit, OnDestroy {
    readonly dialogRef = inject(MatDialogRef<EditCourtDialogComponent>);
    readonly data = inject<any>(MAT_DIALOG_DATA);
    
    courtForm: UntypedFormGroup;
    isLoading: boolean = false;
    private _unsubscribeAll: Subject<any> = new Subject<any>();
    
    // Court properties
    courtId: string = '';
    venueId: string = '';
    venueName: string = '';
    companyName: string = '';
    
    // Sports list - NEW: Load from API
    allSports: any[] = [];
    sportColors: { [key: string]: string } = {};
    sportLogos: { [key: string]: string } = {};
    sportNames: string[] = []; // Array of sport names for dropdown
    
    // Pictures management
    pictures: (string | File)[] = [];
    newPictures: File[] = [];
    picturesToRemove: string[] = [];
    private filePreviewUrls: Map<File, string> = new Map();
    @ViewChild('fileInput') fileInput!: ElementRef<HTMLInputElement>;
    
    // Court types
    courtTypes: string[] = ['indoor', 'outdoor'];
    
    // Surface types
    surfaceTypes: string[] = [
        'hard', 'clay', 'grass', 'carpet', 'synthetic', 
        'concrete', 'asphalt', 'wood', 'turf', 'artificial grass'
    ];

    constructor(
        private _formBuilder: UntypedFormBuilder,
        private courtsService: CourtsService,
        private snackBar: MatSnackBar,
        private cdr: ChangeDetectorRef,
        private dialog: MatDialog
    ) {
        this.courtForm = this._formBuilder.group({
            name: ['', Validators.required],
            sports: ['', Validators.required], // Changed from string to array for multiple sports
            status: ['active', Validators.required],
            courtType: ['', Validators.required],
            surfaceType: ['', Validators.required],
            airConditioned: [false],
            minCapacity: [2, [Validators.required, Validators.min(1)]],
            maxCapacity: [20, [Validators.required, Validators.min(1)]],
            pricePerHour: [0, [Validators.required, Validators.min(0)]]
        });
    }

    ngOnInit(): void {
        this.loadAllSports();
        if (this.data?.court) {
            this.loadCourtData(this.data.court);
        }
        this.cdr.markForCheck();
    }

    private loadAllSports(): void {
        this.courtsService.getAllSports()
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe({
                next: (response) => {
                    // Extract sports data from response
                    // Handle different response structures
                    let sportsData = [];
                    
                    if (Array.isArray(response)) {
                        sportsData = response;
                    } else if (response?.data && Array.isArray(response.data)) {
                        sportsData = response.data;
                    } else if (response?.results && Array.isArray(response.results)) {
                        sportsData = response.results;
                    } else if (response?.sports && Array.isArray(response.sports)) {
                        sportsData = response.sports;
                    }
                    
                    this.allSports = sportsData;
                    
                    // Create lookup maps and sport names array
                    sportsData.forEach((sport: any) => {
                        const sportName = sport.name || sport.sportName || 'Unknown';
                        const sportColor = sport.color || '#6B7280';
                        const sportLogo = sport.logo || sport.icon || '';
                        
                        this.sportColors[sportName] = sportColor;
                        this.sportLogos[sportName] = sportLogo;
                    });
                    
                    // Create array of sport names for dropdown
                    this.sportNames = sportsData.map((sport: any) => 
                        sport.name || sport.sportName || 'Unknown'
                    ).sort();
                    this.normalizeSelectedSport();
                    
                    this.cdr.markForCheck();
                },
                error: (err) => {
                    console.error('Error loading sports:', err);
                    // Fallback to default sports if API fails
                    this.initializeDefaultSports();
                    this.snackBar.open('Error loading sports data', 'Close', { duration: 3000 });
                    this.cdr.markForCheck();
                }
            });
    }

    private initializeDefaultSports(): void {
        // Default sports as fallback
        const defaultSports = [
            { name: 'Badminton', color: '#FF6B6B', logo: '' },
            { name: 'Basketball', color: '#4ECDC4', logo: '' },
            { name: 'Cricket', color: '#45B7D1', logo: '' },
            { name: 'Football', color: '#96CEB4', logo: '' },
            { name: 'Futsal', color: '#FFEAA7', logo: '' },
            { name: 'Hockey', color: '#DDA0DD', logo: '' },
            { name: 'Squash', color: '#98D8C8', logo: '' },
            { name: 'Table Tennis', color: '#F7DC6F', logo: '' },
            { name: 'Tennis', color: '#BB8FCE', logo: '' },
            { name: 'Volleyball', color: '#85C1E9', logo: '' }
        ];
        
        this.allSports = defaultSports;
        this.sportNames = defaultSports.map(sport => sport.name).sort();
        
        defaultSports.forEach(sport => {
            this.sportColors[sport.name] = sport.color;
            this.sportLogos[sport.name] = sport.logo;
        });
        this.normalizeSelectedSport();
    }

    private normalizeSelectedSport(): void {
        const currentValue = this.courtForm.get('sports')?.value;
        const currentIdentity = normalizeSportIdentity(currentValue);
        if (!currentIdentity || this.allSports.length === 0) return;

        const matchingSport = this.allSports.find((sport: any) =>
            [sport.key, sport.name, sport.sportName, sport.code].some(
                value => normalizeSportIdentity(value) === currentIdentity
            )
        );
        const stableValue = matchingSport?.key || matchingSport?.name || matchingSport?.sportName;
        if (stableValue) {
            this.courtForm.patchValue({ sports: stableValue }, { emitEvent: false });
        }
    }

    private loadCourtData(court: any): void {
        // Reset all picture-related state first
        this.cleanupFilePreviewUrls();
        this.pictures = [];
        this.newPictures = [];
        this.picturesToRemove = [];
        
        // Set IDs
        this.courtId = court._id;
        this.venueId = court.venueId;
        this.venueName = court.venueName || 'Unknown Venue';
        this.companyName = court.companyName || 'Unknown Company';
        
        // Handle sports data - could be string or array
        let sportsValue = court.sports || '';
        if (Array.isArray(court.sports)) {
            // If it's an array, take the first sport or join if multiple
            sportsValue = court.sports[0] || '';
        }
        
        // Load basic info
        this.courtForm.patchValue({
            name: court.name || '',
            sports: sportsValue,
            status: court.status || 'active',
            courtType: court.courtType || '',
            surfaceType: court.surfaceType || '',
            airConditioned: court.airConditioned || false,
            minCapacity: court.minCapacity || 2,
            maxCapacity: court.maxCapacity || 20,
            pricePerHour: court.pricePerHour || 0
        });
        this.normalizeSelectedSport();
        
        // Load pictures - ensure they are all strings (URLs), not File objects
        if (court.pictures && Array.isArray(court.pictures)) {
            this.pictures = court.pictures
                .filter((pic: any) => {
                    // Only keep string URLs, filter out any File objects or invalid data
                    return pic && typeof pic === 'string' && pic.trim().length > 0;
                })
                .map((pic: string) => pic.trim());
        } else {
            this.pictures = [];
        }
        
        // Ensure no File objects are in the pictures array after loading
        this.pictures = this.pictures.filter(pic => typeof pic === 'string');

        this.cdr.markForCheck();
    }

    private cleanupFilePreviewUrls(): void {
        // Clean up all file preview URLs
        this.filePreviewUrls.forEach(url => {
            try {
                URL.revokeObjectURL(url);
            } catch (e) {
                console.warn('Error revoking object URL:', e);
            }
        });
        this.filePreviewUrls.clear();
    }

    // Picture handling methods
    openFilePicker(): void {
        if (this.fileInput && this.fileInput.nativeElement) {
            this.fileInput.nativeElement.click();
        }
    }

    onPictureSelect(event: Event): void {
        const input = event.target as HTMLInputElement;
        if (input.files && input.files.length > 0) {
            const files = Array.from(input.files);
            files.forEach(file => {
                this.newPictures.push(file);
                this.pictures.push(file);
            });
            input.value = '';
            this.cdr.markForCheck();
        }
    }

    removePicture(index: number): void {
        const picture = this.pictures[index];
        
        // If it's a URL (existing picture), add to removal list
        if (typeof picture === 'string') {
            this.picturesToRemove.push(picture);
        }
        // If it's a File (newly uploaded), remove from newPictures and clean up preview URL
        else if (picture instanceof File) {
            const fileIndex = this.newPictures.findIndex(f => f === picture);
            if (fileIndex > -1) {
                this.newPictures.splice(fileIndex, 1);
            }
            // Clean up the preview URL
            if (this.filePreviewUrls.has(picture)) {
                URL.revokeObjectURL(this.filePreviewUrls.get(picture)!);
                this.filePreviewUrls.delete(picture);
            }
        }
        
        this.pictures.splice(index, 1);
        this.cdr.markForCheck();
    }

    isFile(picture: any): picture is File {
        return picture instanceof File;
    }

    getFilePreview(file: File | string): string {
        // Safety check - ensure it's actually a File object
        if (!(file instanceof File)) {
            // If it's a string, return it as-is
            if (typeof file === 'string') {
                return file;
            }
            // Otherwise, return empty string to prevent errors
            return '';
        }
        
        if (!this.filePreviewUrls.has(file)) {
            try {
                const url = URL.createObjectURL(file);
                this.filePreviewUrls.set(file, url);
            } catch (error) {
                console.error('Error creating object URL:', error);
                return '';
            }
        }
        return this.filePreviewUrls.get(file) || '';
    }

    trackByPicture(index: number, picture: string | File): string {
        if (typeof picture === 'string') {
            return picture; // Use the URL as the track key
        } else {
            // For File objects, use a combination of name, size, and lastModified
            return `${picture.name}-${picture.size}-${picture.lastModified}`;
        }
    }

    openImageViewer(index: number): void {
        if (index < 0 || index >= this.pictures.length) return;

        // Create a shallow copy to avoid reference issues, but keep the same File/string objects
        const imagesCopy = [...this.pictures];
        
        const dialogData: ImageViewerDialogData = {
            images: imagesCopy,
            currentIndex: index
        };

        this.dialog.open(ImageViewerDialogComponent, {
            data: dialogData,
            panelClass: 'image-viewer-dialog',
            maxWidth: '90vw',
            maxHeight: '90vh'
        });
    }

    getSportColor(sportName: string): string {
        return this.sportColors[sportName] || '#6B7280';
    }

    getSportLogo(sportName: string): string {
        return this.sportLogos[sportName] || '';
    }

    // Get sport object by name
    getSportByName(sportName: string): any {
        return this.allSports.find(sport => 
            sport.name === sportName || sport.sportName === sportName
        );
    }

    // Form validation
    validateForm(): string[] {
        const errors: string[] = [];
        const formValue = this.courtForm.value;
        
        if (formValue.minCapacity > formValue.maxCapacity) {
            errors.push('Minimum capacity cannot be greater than maximum capacity');
        }
        
        if (formValue.pricePerHour < 0) {
            errors.push('Price per hour cannot be negative');
        }
        
        return errors;
    }

    // Build FormData for submission - UPDATED FOR SUPER ADMIN API
    buildFormData(): FormData {
        const formValue = this.courtForm.value;
        const formData = new FormData();
        
        // Append all form fields
        Object.keys(formValue).forEach(key => {
            if (formValue[key] !== null && formValue[key] !== undefined) {
                if (Array.isArray(formValue[key])) {
                    // Handle arrays (like multiple sports)
                    formValue[key].forEach((item: any) => {
                        formData.append(`${key}[]`, String(item));
                    });
                } else {
                    formData.append(key, String(formValue[key]));
                }
            }
        });
        
        // Append pictures - only existing URLs (strings), new Files are handled separately
        this.pictures.forEach(picture => {
            if (picture instanceof File) {
                // New file uploads
                formData.append('pictures', picture, picture.name);
            } else if (typeof picture === 'string' && picture.trim()) {
                // Existing picture URLs to keep
                formData.append('pictures', picture.trim());
            }
        });
        
        // Append pictures to remove
        this.picturesToRemove.forEach(url => {
            formData.append('removePictures', url);
        });
        
        return formData;
    }

    // Build plain object for submission (if no files)
    buildCourtObject(): any {
        const formValue = this.courtForm.value;
        
        // Include minCapacity if it exists in the form
        const courtData: any = {
            name: formValue.name,
            sports: formValue.sports,
            status: formValue.status,
            courtType: formValue.courtType,
            surfaceType: formValue.surfaceType,
            airConditioned: formValue.airConditioned,
            minCapacity: formValue.minCapacity,
            maxCapacity: formValue.maxCapacity,
            pricePerHour: formValue.pricePerHour
        };
        
        // Add pictures - only string URLs, not File objects
        courtData.pictures = this.pictures.filter(pic => typeof pic === 'string');
        
        // Add removePictures if any
        if (this.picturesToRemove.length > 0) {
            courtData.removePictures = this.picturesToRemove;
        }
        
        return courtData;
    }

    // UPDATED onSubmit method to use super admin API
    onSubmit(): void {
        const errors = this.validateForm();
        if (this.courtForm.invalid || this.isLoading || errors.length > 0) {
            if (errors.length > 0) {
                this.snackBar.open(errors[0], 'Close', { duration: 3000 });
            }
            this.cdr.markForCheck();
            return;
        }

        this.isLoading = true;
        this.cdr.markForCheck();

        // Check if we have any new pictures (Files)
        const hasNewPictures = this.newPictures.length > 0;
        const hasPicturesToRemove = this.picturesToRemove.length > 0;
        
        // If we have new pictures or pictures to remove, use FormData
        // Otherwise use plain object
        let requestData: any;
        
        if (hasNewPictures || hasPicturesToRemove) {
            requestData = this.buildFormData();
        } else {
            requestData = this.buildCourtObject();
        }

        // Use the new super admin API
        this.courtsService.updateCourtAsSuperAdmin(this.venueId, this.courtId, requestData)
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe({
                next: (response) => {
                    // Clean up file preview URLs after successful save
                    this.cleanupFilePreviewUrls();
                    
                    // Reset picture state - convert any File objects to empty array
                    // The backend will return the updated pictures as URLs
                    this.newPictures = [];
                    
                    this.snackBar.open('Court updated successfully', 'Close', { duration: 3000 });
                    this.dialogRef.close(response);
                    this.cdr.markForCheck();
                },
                error: (err) => {
                    console.error('Error updating court as super admin:', err);
                    const errorMessage = err.error?.message || err.message || 'Unknown error';
                    this.snackBar.open(`Error updating court: ${errorMessage}`, 'Close', { duration: 5000 });
                    this.isLoading = false;
                    this.cdr.markForCheck();
                }
            });
    }

    onCancel(): void {
        this.dialogRef.close();
    }

    ngOnDestroy(): void {
        // Clean up all file preview URLs
        this.cleanupFilePreviewUrls();
        
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();
    }
}
